"use client"

import { useId, useMemo, useState } from "react"
import { Check, ChevronsUpDown } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

export type VoiceLanguageOption = {
  value: string
  label: string
}

type VoiceLanguageSelectorProps = {
  options: readonly VoiceLanguageOption[]
  value: string[]
  onChange: (languages: string[]) => void
  disabled?: boolean
  loading?: boolean
  error?: string | null
}

export function VoiceLanguageSelector({
  options,
  value,
  onChange,
  disabled = false,
  loading = false,
  error,
}: VoiceLanguageSelectorProps) {
  const [open, setOpen] = useState(false)
  const triggerId = useId()
  const optionLabels = useMemo(
    () => new Map(options.map((option) => [option.value, option.label])),
    [options],
  )
  const selectionSummary = getSelectionSummary(value, optionLabels)
  const placeholder = getPlaceholder({ loading, error: Boolean(error) })

  const toggleLanguage = (language: string) => {
    if (!value.includes(language)) {
      onChange([...value, language])
      return
    }

    onChange(value.filter((selected) => selected !== language))
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={triggerId}>Languages *</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={triggerId}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="Languages"
            disabled={disabled || options.length === 0}
            className="w-full justify-between font-normal"
          >
            <span
              className={cn(
                "truncate text-left",
                value.length === 0 && "text-muted-foreground",
              )}
            >
              {selectionSummary || placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-0"
        >
          <Command>
            <CommandInput placeholder="Search languages..." />
            <CommandList aria-multiselectable="true">
              <CommandEmpty>No languages found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const selectedIndex = value.indexOf(option.value)
                  const isSelected = selectedIndex >= 0
                  const isPrimary = selectedIndex === 0
                  return (
                    <CommandItem
                      key={option.value}
                      value={`${option.label} ${option.value}`}
                      onSelect={() => toggleLanguage(option.value)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span>{option.label}</span>
                      {isPrimary ? (
                        <span className="ml-auto text-xs text-muted-foreground">
                          Primary
                        </span>
                      ) : null}
                      <span className="sr-only">
                        {isSelected ? "Selected" : "Not selected"}
                      </span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <p className="text-xs text-muted-foreground">
        The first selected language is primary. The agent can switch between
        selected languages automatically.
      </p>
    </div>
  )
}

function getSelectionSummary(
  languages: string[],
  optionLabels: Map<string, string>,
): string {
  return languages
    .map((language, index) => {
      const label = optionLabels.get(language) ?? language.toUpperCase()
      return index === 0 ? `${label} (Primary)` : label
    })
    .join(", ")
}

function getPlaceholder({
  loading,
  error,
}: {
  loading: boolean
  error: boolean
}): string {
  if (loading) return "Loading languages..."
  if (error) return "Unable to load languages"
  return "Select languages"
}
